<template>
  <a-modal
    v-if="visible"
    title="Shift Sign"
    :visible="visible"
    data-testid="shifts-sign-modal"
    @cancel="closeModal"
    :destroy-on-close="true"
  >
    <div v-if="loadingShift || !editingShift" class="loading-spin" style="height: 650px" data-testid="shifts-sign-loading">
      <a-spin />
    </div>
    <div v-else data-testid="shifts-sign-content">
      <template v-if="editingShift.version === 0">
        <p class="font-bold">
          Associate hasn’t signed the shift even once, so it doesn’t contain any Signed Times. If you sign it, the time
          will be recorded based on the Tracked and Scheduled sections.
        </p>
      </template>
      <div class="pb-4">
        <a-tabs default-active-key="signed" size="large" data-testid="shifts-sign-tabs">
          <a-tab-pane key="signed" tab="Signed">
            <shift-time :times="signedTime" />
          </a-tab-pane>
          <a-tab-pane key="actual" tab="Tracked">
            <shift-time :times="actualTime" />
          </a-tab-pane>
          <a-tab-pane key="scheduled" tab="Scheduled">
            <shift-time :times="scheduledTime" />
          </a-tab-pane>
        </a-tabs>
      </div>
      <div class="flex flex-column gap-2 mb-3">
        <label for="shift-sign-modal-explanation">Explanation (required)</label>
        <a-textarea
          id="shift-sign-modal-explanation"
          v-model="explanation"
          :max-length="2048"
          :auto-size="{ minRows: 3, maxRows: 6 }"
          aria-required="true"
          data-testid="shifts-sign-modal-explanation"
        />
      </div>
      <signature
        checkbox-label="I agree that all data is true and correct"
        :available-signature="null"
        ref="signatureRef"
        data-testid="shifts-sign-signature"
        @checked="setSignatureCheckboxValue"
        @save-signature="setSignatureId"
      />
    </div>

    <template slot="footer">
      <div class="flex justify-content-end" data-testid="shifts-sign-footer">
        <a-button
          type="primary"
          :disabled="!checked || !!signingExplanationError"
          :loading="loadingSave"
          data-testid="shifts-sign-submit-button"
          @click="openConfirmModal"
        >
          Sign
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Button, Input, Modal, notification, Spin, Tabs } from 'ant-design-vue'
import shiftsHelper from '@/helpers/shifts'
import { createNamespacedHelpers } from 'vuex'
import Signature from '@/components/signature.vue'
import ShiftTime from '../shift-time.vue'

const { mapState: shiftState, mapActions: shiftActions } = createNamespacedHelpers('shifts')

export default {
  components: {
    'a-modal': Modal,
    'a-button': Button,
    'a-textarea': Input.TextArea,
    'a-spin': Spin,
    'a-tabs': Tabs,
    'a-tab-pane': Tabs.TabPane,
    signature: Signature,
    'shift-time': ShiftTime
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    info: {
      type: Object,
      default: () => ({
        shift_id: undefined
      })
    },
    edit: {
      type: Function
    }
  },
  mixins: [shiftsHelper],
  data() {
    return {
      loadingShift: false,
      loadingSave: false,

      signature_id: undefined,
      checked: false,
      explanation: '',

      editingShift: {}
    }
  },
  watch: {
    visible(newVal) {
      this.explanation = ''
      this.signature_id = undefined
      this.checked = false
      if (newVal && this.info.shift_id && this.shift?.id !== this.info.shift_id) {
        this.fetchShift()
      }
    },
    shift: {
      handler(newVal) {
        if (newVal) {
          const isVersionZero = newVal.version === 0
          if (isVersionZero) {
            this.editingShift = {
              ...newVal,
              work_start: newVal.work_start ?? newVal.actual_work_start ?? newVal.scheduled_start,
              work_end: newVal.work_end ?? newVal.actual_work_end ?? newVal.scheduled_end,
              lunch_start: newVal.lunch_start ?? newVal.actual_lunch_start,
              lunch_end: newVal.lunch_end ?? newVal.actual_lunch_end,
              second_lunch_start: newVal.second_lunch_start ?? newVal.actual_second_lunch_start,
              second_lunch_end: newVal.second_lunch_end ?? newVal.actual_second_lunch_end
            }
          } else {
            this.editingShift = {
              ...newVal,
              modified_by_admin: true,
              rest_break_checked: true
            }
          }
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    ...shiftState({
      shift: state => state.shift.shift
    }),

    signingExplanationError() {
      if (!this.explanation.trim()) return 'Please explain why you are signing this shift yourself.'
      if (this.explanation.trim().length > 2048) return 'Explanation must be 2048 characters or fewer.'
      return null
    },

    scheduledTime() {
      return this.generateTimeData('scheduled_')
    },

    actualTime() {
      return this.generateTimeData('actual_')
    },

    signedTime() {
      return this.generateTimeData('')
    }
  },
  methods: {
    ...shiftActions(['getAllShiftInfo', 'postSignShift', 'updateShift']),

    setSignatureCheckboxValue(val) {
      this.checked = val
    },

    async setSignatureId(signatureId) {
      if (this.signingExplanationError) {
        this.showNotification('warning', 'Explanation is required', this.signingExplanationError)
        return
      }
      this.signature_id = signatureId

      this.loadingSave = true

      try {
        const isVersionZero = this.editingShift.version === 0
        const payload = isVersionZero
          ? {
              shift: {
                ...this.editingShift,
                explanation: this.explanation.trim(),
                modified_by_admin: true,
                rest_break_checked: true,
                signature: { id: this.signature_id }
              }
            }
          : {
              shift_id: this.editingShift.id,
              version_id: this.editingShift.version,
              signature_id: this.signature_id,
              explanation: this.explanation.trim()
            }

        const requestMethod = isVersionZero ? this.updateShift : this.postSignShift

        await requestMethod(payload)

        this.showNotification('success', 'Success', 'The shift has been signed')

        this.$emit('edit')
        this.closeModal()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingSave = false
      }
    },

    closeModal() {
      this.$emit('update:visible', false)
    },

    generateTimeData(prefix) {
      return {
        scheduled_start: this.editingShift.scheduled_start,
        scheduled_end: this.editingShift.scheduled_end,
        start: prefix === 'scheduled_' ? this.editingShift[`${prefix}start`] : this.editingShift[`${prefix}work_start`],
        end: prefix === 'scheduled_' ? this.editingShift[`${prefix}end`] : this.editingShift[`${prefix}work_end`],
        lunch_start: this.editingShift[`${prefix}lunch_start`] || null,
        lunch_end: this.editingShift[`${prefix}lunch_end`] || null,
        second_lunch_start: this.editingShift[`${prefix}second_lunch_start`] || null,
        second_lunch_end: this.editingShift[`${prefix}second_lunch_end`] || null,
        work_hours: this.editingShift.hours[`${prefix}work_hours`] * 60 || null,
        pay_hours: this.editingShift.hours[`${prefix}pay_hours`] * 60 || null,
        duration: this.editingShift.hours[`${prefix}duration`] * 60 || null
      }
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    async fetchShift() {
      try {
        this.loadingShift = true

        const payload = {
          ...this.info
        }

        await this.getAllShiftInfo(payload)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingShift = false
      }
    },

    openConfirmModal() {
      if (this.signingExplanationError) {
        this.showNotification('warning', 'Explanation is required', this.signingExplanationError)
        return
      }
      if (this.editingShift.payment_type === 'Employee') {
        Modal.confirm({
          title: 'Are you sure?',
          content:
            'Are you sure you want to sign this shift on behalf of the EMPLOYEE? This could lead to legal issues down the line. It’s best to only do this in rare, exceptional cases.',

          onOk: () => {
            this.signShift()
          },
          onCancel() {}
        })
      } else {
        this.signShift()
      }
    },

    async signShift() {
      if (this.signingExplanationError) {
        this.showNotification('warning', 'Explanation is required', this.signingExplanationError)
        return
      }
      await this.$refs.signatureRef.saveSignature()
    }
  }
}
</script>

<style scoped>
.ant-card {
  height: fit-content;
}

.ant-tabs {
  border: 1px solid var(--border-color-base);
  border-radius: 10px;
}

::v-deep .ant-tabs-bar {
  margin: 0;
}
</style>
